import { motion } from "framer-motion";
import { EnvelopeSimple, Phone, MapPin, ArrowRight, ShieldCheck, Clock } from "@phosphor-icons/react";

export default function Contact() {
    return (
        <section id="contact" data-testid="contact-section" className="relative py-24 lg:py-32 bg-lumos-midnight text-white overflow-hidden">
            <div className="absolute inset-0 bg-grid-dark opacity-50" />
            <div className="absolute -top-32 right-0 w-[560px] h-[560px] rounded-full bg-lumos-blue/20 blur-3xl" />
            <div className="absolute -bottom-40 -left-20 w-[480px] h-[480px] rounded-full bg-lumos-green/15 blur-3xl" />

            <div className="relative max-w-6xl mx-auto px-5 sm:px-8 lg:px-10">
                <div className="text-center mb-14">
                    <span className="eyebrow !text-lumos-green justify-center">Contact direct</span>
                    <motion.h2
                        initial={{ opacity: 0, y: 22 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                        className="mt-4 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight"
                    >
                        Parlons de<br />
                        <span className="font-serif-display text-lumos-blue">votre projet.</span>
                    </motion.h2>
                    <p className="mt-6 text-white/70 text-lg max-w-xl mx-auto">
                        Un expert RGE vous rappelle sous 24h. Devis gratuit, sans engagement.
                    </p>
                </div>

                {/* MAIN CONTACT BLOCKS */}
                <div className="grid md:grid-cols-2 gap-5 mb-10">
                    {/* EMAIL */}
                    <motion.a
                        href="mailto:contact@lumosaudit.com"
                        data-testid="contact-email"
                        initial={{ opacity: 0, y: 22 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="group relative rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 hover:border-lumos-blue/60 p-8 lg:p-10 transition-all duration-500 hover:bg-white/[0.08] overflow-hidden"
                    >
                        <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-lumos-blue/15 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
                        <div className="relative flex items-start justify-between mb-6">
                            <div className="w-14 h-14 rounded-2xl bg-lumos-blue/20 border border-lumos-blue/40 grid place-items-center text-lumos-blue">
                                <EnvelopeSimple size={26} weight="duotone" />
                            </div>
                            <ArrowRight size={22} weight="bold" className="text-white/40 group-hover:text-lumos-blue group-hover:translate-x-1 transition-all" />
                        </div>
                        <div className="text-xs font-bold tracking-widest uppercase text-white/50 mb-2">Email</div>
                        <div className="relative font-display text-2xl md:text-3xl lg:text-[34px] font-extrabold leading-tight break-all">
                            contact@<wbr />lumosaudit.com
                        </div>
                        <div className="mt-4 text-sm text-white/60">Réponse sous 24h ouvrées</div>
                    </motion.a>

                    {/* PHONE */}
                    <motion.a
                        href="tel:+33186958700"
                        data-testid="contact-phone"
                        initial={{ opacity: 0, y: 22 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="group relative rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 hover:border-lumos-green/60 p-8 lg:p-10 transition-all duration-500 hover:bg-white/[0.08] overflow-hidden"
                    >
                        <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-lumos-green/15 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
                        <div className="relative flex items-start justify-between mb-6">
                            <div className="w-14 h-14 rounded-2xl bg-lumos-green/20 border border-lumos-green/40 grid place-items-center text-lumos-green">
                                <Phone size={26} weight="duotone" />
                            </div>
                            <ArrowRight size={22} weight="bold" className="text-white/40 group-hover:text-lumos-green group-hover:translate-x-1 transition-all" />
                        </div>
                        <div className="text-xs font-bold tracking-widest uppercase text-white/50 mb-2">Téléphone</div>
                        <div className="relative font-display text-3xl md:text-4xl lg:text-[44px] font-extrabold leading-tight tracking-tight">
                            01.86.95.87.00
                        </div>
                        <div className="mt-4 text-sm text-white/60">Lundi au samedi · 8h-19h</div>
                    </motion.a>
                </div>

                {/* ADDRESS */}
                <motion.a
                    href="https://maps.google.com/?q=10+rue+du+Colisée+75008+Paris"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-address"
                    initial={{ opacity: 0, y: 22 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.15 }}
                    className="group relative rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 hover:border-lumos-amber/60 p-7 lg:p-8 transition-all duration-500 hover:bg-white/[0.08] flex flex-col md:flex-row md:items-center gap-5 md:gap-8 overflow-hidden"
                >
                    <div className="flex items-center gap-5">
                        <div className="w-14 h-14 rounded-2xl bg-lumos-amber/20 border border-lumos-amber/40 grid place-items-center text-lumos-amber flex-shrink-0">
                            <MapPin size={26} weight="duotone" />
                        </div>
                        <div>
                            <div className="text-xs font-bold tracking-widest uppercase text-white/50 mb-1">Adresse</div>
                            <div className="font-display text-xl lg:text-2xl font-bold leading-tight">10 rue du Colisée, 75008 Paris</div>
                        </div>
                    </div>
                    <div className="md:ml-auto flex items-center gap-2 text-sm font-semibold text-white/70 group-hover:text-lumos-amber transition-colors">
                        Voir sur la carte
                        <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-1" />
                    </div>
                </motion.a>

                {/* TRUST BAR */}
                <div className="mt-10 flex flex-wrap justify-center gap-x-8 gap-y-4 text-sm text-white/65">
                    <span className="flex items-center gap-2"><ShieldCheck size={18} weight="bold" className="text-lumos-green" /> Certifié RGE</span>
                    <span className="flex items-center gap-2"><Clock size={18} weight="bold" className="text-lumos-blue" /> Rapport sous 24-48h</span>
                    <span className="flex items-center gap-2"><MapPin size={18} weight="bold" className="text-lumos-amber" /> Île-de-France · Oise · Aisne</span>
                </div>
            </div>
        </section>
    );
}
